import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const TONES = {
  success: "bg-emerald-50 text-emerald-800 border-emerald-300",
  warning: "bg-amber-50 text-amber-800 border-amber-300",
  danger: "bg-rose-50 text-rose-800 border-rose-300",
  info: "bg-blue-50 text-blue-800 border-blue-300",
  neutral: "bg-slate-100 text-slate-700 border-slate-300",
};
const STATUS_TONE = {
  approved: "success", confirmed: "success", posted: "success", verified: "success", paid: "success", active: "success", completed: "success", enabled: "success",
  submitted: "info", under_review: "info", pending_inspection: "info",
  draft: "neutral", not_assessed: "neutral", unpaid: "neutral", retired: "neutral", disabled: "neutral",
  pending_verification: "warning", pending_approval: "warning", additional_info_required: "warning", email_verification_pending: "warning",
  awaiting_wht_review: "warning", payment_under_review: "warning", pending: "warning", partially_paid: "warning",
  rejected: "danger", suspended: "danger", not_eligible: "danger", wht_rejected: "danger",
};
const LABELS = { confirmed: "Eligible / Confirmed", not_eligible: "Not Eligible / N.A.", not_assessed: "Not Assessed" };

export const statusLabel = (s) => LABELS[s] || (s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function StatusBadge({ status, tone, children, testId }) {
  return (
    <Badge variant="outline" data-testid={testId} className={cn("font-medium whitespace-nowrap", TONES[tone || STATUS_TONE[status] || "neutral"])}>
      {children || statusLabel(status)}
    </Badge>
  );
}

export function PageHeader({ eyebrow, title, description, actions }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 fade-up">
      <div>
        {eyebrow && <p className="text-xs font-semibold uppercase tracking-[0.18em] text-amber-700 mb-2">{eyebrow}</p>}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h1>
        {description && <p className="text-sm text-slate-500 mt-2 max-w-2xl">{description}</p>}
      </div>
      {actions && <div className="flex gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export function Panel({ title, description, actions, children, className, testId }) {
  return (
    <section data-testid={testId} className={cn("bg-white border border-slate-200 rounded-lg shadow-sm", className)}>
      {(title || actions) && (
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-100">
          <div>
            <h3 className="font-semibold text-slate-900">{title}</h3>
            {description && <p className="text-xs text-slate-500 mt-1">{description}</p>}
          </div>
          {actions}
        </div>
      )}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function Kpi({ label, value, hint, testId, tone = "slate", to }) {
  const ring = { amber: "border-l-amber-500", rose: "border-l-rose-500", emerald: "border-l-emerald-500", blue: "border-l-blue-500", slate: "border-l-slate-400" }[tone];
  const body = (
    <div className={cn("bg-white border border-slate-200 border-l-4 rounded-lg p-5 shadow-sm h-full min-w-0 transition-colors hover:border-amber-400", ring)}>
      <p className="text-xs font-medium uppercase tracking-wider text-slate-500">{label}</p>
      <p className="font-display text-xl sm:text-2xl xl:text-3xl font-bold text-slate-900 mt-2 break-words" data-testid={testId}>{value}</p>
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
  return to ? <a href={to}>{body}</a> : body;
}

export function NativeSelect({ value, onChange, options, placeholder, testId, className, disabled }) {
  return (
    <select data-testid={testId} disabled={disabled} value={value ?? ""} onChange={(e) => onChange(e.target.value)}
      className={cn("h-9 w-full rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring disabled:opacity-50", className)}>
      {placeholder !== undefined && <option value="">{placeholder}</option>}
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function InfoRow({ label, children, testId }) {
  return (
    <div className="flex items-start justify-between gap-4 py-3 border-b border-slate-100 last:border-0">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900 text-right" data-testid={testId}>{children}</span>
    </div>
  );
}

export function Empty({ children }) {
  return <div className="text-sm text-slate-500 py-10 text-center border border-dashed border-slate-200 rounded-lg">{children}</div>;
}
